import { type ReactNode, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import {
  ArrowRight,
  BriefcaseBusiness,
  CalendarDays,
  Clock3,
  Home,
  ShieldCheck,
  Star,
  ToggleLeft,
  ToggleRight,
  Wallet,
  X,
} from 'lucide-react'
import { Link, Navigate } from 'react-router-dom'
import Button from '../components/ui/Button'
import FormError from '../components/ui/FormError'
import {
  ACCEPT_BOOKING_MUTATION,
  DECLINE_BOOKING_MUTATION,
  MY_PROVIDER_PROFILE_QUERY,
  PROVIDER_DASHBOARD_BOOKINGS_QUERY,
  SET_VACATION_MODE_MUTATION,
  START_BOOKING_MUTATION,
  type AcceptBookingData,
  type AcceptBookingVariables,
  type DashboardBooking,
  type DeclineBookingData,
  type DeclineBookingVariables,
  type MyProviderProfileData,
  type ProviderDashboardBookingsData,
  type ProviderDashboardProfile,
  type SetVacationModeData,
  type SetVacationModeVariables,
  type StartBookingData,
  type StartBookingVariables,
} from '../graphql/providerDashboard'
import { useAuth } from '../contexts/AuthContext'
import { useLang, useScopedTranslations } from '../contexts/LangContext'
import { localized, normalizeLocale, type SupportedLocale } from '../lib/i18n'
import { isOnboardingComplete } from '../lib/providerOnboarding'

const translations = {
  el: {
    eyebrow: 'Provider dashboard',
    pageTitle: 'Οργάνωσε τα ραντεβού σου σε ένα σημείο',
    pageBody: 'Δες τα αιτήματα που χρειάζονται απάντηση, τις επόμενες δουλειές και τα βασικά metrics της εβδομάδας.',
    loadingTitle: 'Φορτώνουμε το dashboard...',
    missingProfileTitle: 'Λείπει προφίλ επαγγελματία',
    missingProfileBody: 'Ο λογαριασμός σου δεν έχει ακόμη ενεργό provider profile.',
    pendingTitle: 'Αιτήματα σε αναμονή',
    upcomingTitle: 'Επόμενες δουλειές',
    completedTitle: 'Πρόσφατα ολοκληρωμένες',
    quickLinksTitle: 'Γρήγορες κινήσεις',
    statsToday: 'Σημερινά ραντεβού',
    statsWeek: 'Έσοδα εβδομάδας',
    statsRating: 'Μέση αξιολόγηση',
    statsPending: 'Εκκρεμή αιτήματα',
    vacationTitle: 'Vacation mode',
    vacationBody: 'Κλείσε προσωρινά νέες κρατήσεις όταν χρειάζεσαι ανάσα.',
    vacationOn: 'Το vacation mode είναι ενεργό',
    vacationOff: 'Δέχεσαι νέες κρατήσεις',
    vacationUntil: 'Έως',
    turnOn: 'Ενεργοποίηση',
    turnOff: 'Απενεργοποίηση',
    noPendingTitle: 'Δεν υπάρχουν αιτήματα για απάντηση',
    noPendingBody: 'Τα νέα booking requests θα εμφανίζονται εδώ μόλις φτάνουν.',
    noUpcomingTitle: 'Δεν υπάρχουν επιβεβαιωμένες δουλειές',
    noUpcomingBody: 'Οι επόμενες κρατήσεις σου θα εμφανίζονται εδώ μόλις επιβεβαιωθούν.',
    noCompletedTitle: 'Δεν υπάρχουν πρόσφατες ολοκληρώσεις',
    noCompletedBody: 'Οι ολοκληρωμένες δουλειές των τελευταίων 7 ημερών θα εμφανίζονται εδώ.',
    accept: 'Αποδοχή',
    decline: 'Απόρριψη',
    startJob: 'Έναρξη εργασίας',
    starting: 'Έναρξη...',
    customer: 'Πελάτης',
    service: 'Υπηρεσία',
    schedule: 'Πρόγραμμα',
    duration: 'Διάρκεια',
    address: 'Διεύθυνση',
    loadError: 'Δεν ήταν δυνατή η φόρτωση όλων των στοιχείων του dashboard.',
    retry: 'Δοκίμασε ξανά',
    quickLinkProfile: 'Επεξεργασία προφίλ',
    quickLinkAvailability: 'Διαθεσιμότητα',
    quickLinkServices: 'Υπηρεσίες',
    quickLinkProfileBody: 'Ανανέωσε bio, εμπειρία και βασικά στοιχεία.',
    quickLinkAvailabilityBody: 'Διαμόρφωσε το εβδομαδιαίο πρόγραμμά σου.',
    quickLinkServicesBody: 'Δες και οργάνωσε τις υπηρεσίες που προσφέρεις.',
    showCompleted: 'Δες ολοκληρωμένες δουλειές',
    hideCompleted: 'Κρύψε ολοκληρωμένες δουλειές',
    declineTitle: 'Απόρριψη αιτήματος',
    declineBody: 'Πρόσθεσε σύντομο λόγο ώστε ο πελάτης να ξέρει τι συνέβη.',
    declineReasonLabel: 'Λόγος απόρριψης',
    declineReasonPlaceholder: 'Π.χ. Δεν είμαι διαθέσιμος εκείνη την ώρα.',
    declineReasonRequired: 'Συμπλήρωσε λόγο απόρριψης.',
    close: 'Κλείσιμο',
    confirmDecline: 'Επιβεβαίωση απόρριψης',
    actionFailed: 'Η ενέργεια δεν ολοκληρώθηκε. Δοκίμασε ξανά.',
    verificationApproved: 'Επαληθευμένος',
    verificationPending: 'Σε έλεγχο',
    verificationRejected: 'Χρειάζεται ενέργεια',
    verificationDefault: 'Κατάσταση λογαριασμού',
    reviewCount: 'κριτικές',
    jobs: 'δουλειές',
    minutes: 'λεπτά',
    today: 'Σήμερα',
  },
  en: {
    eyebrow: 'Provider dashboard',
    pageTitle: 'Run your work from one place',
    pageBody: 'See requests that need action, upcoming jobs, and the key numbers for your week.',
    loadingTitle: 'Loading dashboard...',
    missingProfileTitle: 'Provider profile missing',
    missingProfileBody: 'This account does not have an active provider profile yet.',
    pendingTitle: 'Pending requests',
    upcomingTitle: 'Upcoming jobs',
    completedTitle: 'Recently completed',
    quickLinksTitle: 'Quick links',
    statsToday: "Today's bookings",
    statsWeek: "This week's earnings",
    statsRating: 'Average rating',
    statsPending: 'Pending requests',
    vacationTitle: 'Vacation mode',
    vacationBody: 'Pause new bookings when you need breathing room.',
    vacationOn: 'Vacation mode is active',
    vacationOff: 'You are accepting new bookings',
    vacationUntil: 'Until',
    turnOn: 'Turn on',
    turnOff: 'Turn off',
    noPendingTitle: 'No requests need a response',
    noPendingBody: 'New booking requests will appear here as they arrive.',
    noUpcomingTitle: 'No confirmed jobs yet',
    noUpcomingBody: 'Your next confirmed bookings will appear here once they are accepted.',
    noCompletedTitle: 'No recent completions',
    noCompletedBody: 'Completed jobs from the last 7 days will appear here.',
    accept: 'Accept',
    decline: 'Decline',
    startJob: 'Start job',
    starting: 'Starting...',
    customer: 'Customer',
    service: 'Service',
    schedule: 'Schedule',
    duration: 'Duration',
    address: 'Address',
    loadError: 'We could not load all dashboard data right now.',
    retry: 'Try again',
    quickLinkProfile: 'Edit profile',
    quickLinkAvailability: 'Availability',
    quickLinkServices: 'Services',
    quickLinkProfileBody: 'Update your bio, experience, and core details.',
    quickLinkAvailabilityBody: 'Shape your weekly schedule.',
    quickLinkServicesBody: 'Review and manage the services you offer.',
    showCompleted: 'Show completed jobs',
    hideCompleted: 'Hide completed jobs',
    declineTitle: 'Decline request',
    declineBody: 'Add a short reason so the customer knows what happened.',
    declineReasonLabel: 'Decline reason',
    declineReasonPlaceholder: 'E.g. I am unavailable at that time.',
    declineReasonRequired: 'Add a decline reason.',
    close: 'Close',
    confirmDecline: 'Confirm decline',
    actionFailed: 'The action could not be completed. Please try again.',
    verificationApproved: 'Verified',
    verificationPending: 'Under review',
    verificationRejected: 'Needs action',
    verificationDefault: 'Account status',
    reviewCount: 'reviews',
    jobs: 'jobs',
    minutes: 'minutes',
    today: 'Today',
  },
} as const

type DashboardCopy = Record<keyof typeof translations.en, string>

function todayIso() {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function isWithinCurrentWeek(dateValue: string) {
  const date = new Date(`${dateValue}T12:00:00`)
  const today = new Date()
  const mondayOffset = (today.getDay() + 6) % 7
  const weekStart = new Date(today)
  weekStart.setDate(today.getDate() - mondayOffset)
  weekStart.setHours(0, 0, 0, 0)
  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekStart.getDate() + 7)

  return date >= weekStart && date < weekEnd
}

function isWithinLast7Days(dateValue: string) {
  const date = new Date(`${dateValue}T12:00:00`)
  const today = new Date()
  const cutoff = new Date(today)
  cutoff.setDate(today.getDate() - 7)
  cutoff.setHours(0, 0, 0, 0)
  today.setHours(23, 59, 59, 999)

  return date >= cutoff && date <= today
}

function normalizeTime(timeValue: string) {
  return timeValue.slice(0, 5)
}

function formatCurrency(cents: number, locale: SupportedLocale) {
  return new Intl.NumberFormat(locale === 'el' ? 'el-GR' : 'en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(cents / 100)
}

function formatDate(dateValue: string, locale: SupportedLocale, copy: DashboardCopy) {
  if (dateValue === todayIso()) return copy.today

  return new Intl.DateTimeFormat(locale === 'el' ? 'el-GR' : 'en-GB', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(new Date(`${dateValue}T12:00:00`))
}

function customerName(booking: DashboardBooking) {
  if (!booking.customer) return ''
  return `${booking.customer.firstName} ${booking.customer.lastName}`
}

function bookingAddress(booking: DashboardBooking) {
  if (!booking.property) return null

  return [booking.property.label, booking.property.addressLine1, booking.property.city, booking.property.postalCode]
    .filter(Boolean)
    .join(', ')
}

function verificationLabel(profile: ProviderDashboardProfile, copy: DashboardCopy) {
  if (profile.verified) return copy.verificationApproved
  if (profile.verificationStatus === 'pending') return copy.verificationPending
  if (profile.verificationStatus === 'rejected') return copy.verificationRejected
  return copy.verificationDefault
}

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-2xl font-bold text-slate-950">{title}</h2>
      <div className="mt-6">{children}</div>
    </section>
  )
}

function StatCard({
  title,
  value,
  icon,
  accent = 'primary',
  detail,
}: {
  title: string
  value: string
  icon: ReactNode
  accent?: 'primary' | 'amber' | 'emerald' | 'slate'
  detail?: string
}) {
  const accentClasses = {
    primary: 'bg-primary-50 text-primary-700',
    amber: 'bg-amber-50 text-amber-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    slate: 'bg-slate-100 text-slate-700',
  }

  return (
    <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-400">{title}</p>
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl ${accentClasses[accent]}`}>
          {icon}
        </span>
      </div>
      <p className="mt-4 text-3xl font-black tracking-tight text-slate-950">{value}</p>
      {detail && <p className="mt-2 text-sm text-slate-500">{detail}</p>}
    </div>
  )
}

function LoadingDashboard() {
  return (
    <div className="mx-auto max-w-6xl animate-pulse space-y-8 px-4 py-8 md:px-6 md:py-10">
      <div className="h-24 rounded-[2rem] bg-slate-200" />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {Array.from({ length: 5 }, (_, index) => (
          <div key={index} className="h-40 rounded-[1.5rem] bg-slate-200" />
        ))}
      </div>
      <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-8">
          <div className="h-80 rounded-[1.75rem] bg-slate-200" />
          <div className="h-80 rounded-[1.75rem] bg-slate-200" />
        </div>
        <div className="space-y-8">
          <div className="h-72 rounded-[1.75rem] bg-slate-200" />
          <div className="h-72 rounded-[1.75rem] bg-slate-200" />
        </div>
      </div>
    </div>
  )
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-6">
      <h3 className="text-lg font-bold text-slate-950">{title}</h3>
      <p className="mt-3 text-sm leading-7 text-slate-600">{body}</p>
    </div>
  )
}

function BookingCard({
  booking,
  locale,
  copy,
  children,
}: {
  booking: DashboardBooking
  locale: SupportedLocale
  copy: DashboardCopy
  children?: ReactNode
}) {
  return (
    <article className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-lg font-bold text-slate-950">{customerName(booking)}</p>
          <p className="mt-2 text-sm font-medium text-slate-500">
            {localized(booking.serviceType ?? { name: '', nameEl: '' }, locale)}
          </p>
        </div>
        <p className="rounded-full bg-white px-3 py-1.5 text-sm font-bold text-primary-700 shadow-sm">
          {formatCurrency(booking.priceCents, locale)}
        </p>
      </div>

      <dl className="mt-5 grid gap-4 text-sm text-slate-600 md:grid-cols-2">
        <div>
          <dt className="font-semibold text-slate-900">{copy.schedule}</dt>
          <dd className="mt-1">
            {formatDate(booking.scheduledDate, locale, copy)} · {normalizeTime(booking.startTime)}-{normalizeTime(booking.endTime)}
          </dd>
        </div>
        <div>
          <dt className="font-semibold text-slate-900">{copy.duration}</dt>
          <dd className="mt-1">{booking.durationMinutes} {copy.minutes}</dd>
        </div>
        <div className="md:col-span-2">
          <dt className="font-semibold text-slate-900">{copy.address}</dt>
          <dd className="mt-1">{bookingAddress(booking) || '-'}</dd>
        </div>
      </dl>

      {children && <div className="mt-5 flex flex-wrap gap-3">{children}</div>}
    </article>
  )
}

function DeclineModal({
  copy,
  loading,
  errors,
  reason,
  onReasonChange,
  onClose,
  onConfirm,
}: {
  copy: DashboardCopy
  loading: boolean
  errors: string[]
  reason: string
  onReasonChange: (value: string) => void
  onClose: () => void
  onConfirm: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4">
      <div className="w-full max-w-lg rounded-[1.75rem] bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-950">{copy.declineTitle}</h2>
            <p className="mt-3 text-sm leading-7 text-slate-600">{copy.declineBody}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200"
            aria-label={copy.close}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-6">
          <FormError errors={errors} />
          <label htmlFor="decline-reason" className="mb-2 mt-4 block text-sm font-semibold text-slate-700">
            {copy.declineReasonLabel}
          </label>
          <textarea
            id="decline-reason"
            value={reason}
            onChange={(event) => onReasonChange(event.target.value)}
            rows={4}
            placeholder={copy.declineReasonPlaceholder}
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100"
          />
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            {copy.close}
          </Button>
          <Button type="button" loading={loading} onClick={onConfirm}>
            {copy.confirmDecline}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default function ProviderDashboardPage() {
  const { currentUser } = useAuth()
  const { locale: appLocale } = useLang()
  const activeLocale = normalizeLocale(currentUser?.locale ?? appLocale)
  const copy = useScopedTranslations('provider.dashboard', translations[activeLocale], activeLocale)

  const [pageErrors, setPageErrors] = useState<string[]>([])
  const [declineBookingId, setDeclineBookingId] = useState<string | null>(null)
  const [declineReason, setDeclineReason] = useState('')
  const [declineErrors, setDeclineErrors] = useState<string[]>([])
  const [actionBookingId, setActionBookingId] = useState<string | null>(null)
  const [showCompleted, setShowCompleted] = useState(false)

  const {
    data: profileData,
    loading: profileLoading,
    error: profileError,
    refetch: refetchProfile,
  } = useQuery<MyProviderProfileData>(MY_PROVIDER_PROFILE_QUERY)

  const profile = profileData?.myProviderProfile ?? null

  const {
    data: bookingsData,
    loading: bookingsLoading,
    error: bookingsError,
    refetch: refetchBookings,
  } = useQuery<ProviderDashboardBookingsData>(PROVIDER_DASHBOARD_BOOKINGS_QUERY, {
    skip: !profile,
    pollInterval: 60000,
  })

  const [acceptBooking, { loading: acceptingBooking }] = useMutation<AcceptBookingData, AcceptBookingVariables>(ACCEPT_BOOKING_MUTATION)
  const [declineBooking, { loading: decliningBooking }] = useMutation<DeclineBookingData, DeclineBookingVariables>(DECLINE_BOOKING_MUTATION)
  const [startBooking, { loading: startingBooking }] = useMutation<StartBookingData, StartBookingVariables>(START_BOOKING_MUTATION)
  const [setVacationMode, { loading: savingVacationMode }] = useMutation<SetVacationModeData, SetVacationModeVariables>(SET_VACATION_MODE_MUTATION)

  const pendingRequests = bookingsData?.pendingRequests.nodes ?? []
  const upcomingBookings = bookingsData?.upcomingBookings.nodes ?? []
  const activeToday = bookingsData?.activeToday.nodes ?? []
  const recentCompleted = bookingsData?.recentCompleted.nodes ?? []

  const todayBookingCount = useMemo(
    () => [pendingRequests, upcomingBookings, activeToday]
      .flat()
      .filter((booking) => booking.scheduledDate === todayIso()).length,
    [activeToday, pendingRequests, upcomingBookings],
  )

  const weeklyEarnings = useMemo(
    () => recentCompleted
      .filter((booking) => isWithinCurrentWeek(booking.scheduledDate))
      .reduce((sum, booking) => sum + booking.priceCents, 0),
    [recentCompleted],
  )

  const completedLast7Days = useMemo(
    () => recentCompleted.filter((booking) => isWithinLast7Days(booking.scheduledDate)),
    [recentCompleted],
  )

  async function refreshDashboard() {
    setPageErrors([])
    await Promise.all([
      refetchProfile(),
      profile ? refetchBookings() : Promise.resolve(),
    ])
  }

  async function handleAcceptBooking(bookingId: string) {
    setActionBookingId(bookingId)
    setPageErrors([])

    try {
      const { data } = await acceptBooking({ variables: { bookingId } })
      const result = data?.acceptBooking

      if (!result || result.errors.length > 0 || !result.booking) {
        setPageErrors(result?.errors.length ? result.errors : [copy.actionFailed])
        return
      }

      await refreshDashboard()
    } catch {
      setPageErrors([copy.actionFailed])
    } finally {
      setActionBookingId(null)
    }
  }

  async function handleDeclineBooking() {
    if (!declineBookingId) return
    if (!declineReason.trim()) {
      setDeclineErrors([copy.declineReasonRequired])
      return
    }

    setDeclineErrors([])
    setActionBookingId(declineBookingId)

    try {
      const { data } = await declineBooking({
        variables: {
          bookingId: declineBookingId,
          reason: declineReason.trim(),
        },
      })
      const result = data?.declineBooking

      if (!result || result.errors.length > 0 || !result.booking) {
        setDeclineErrors(result?.errors.length ? result.errors : [copy.actionFailed])
        return
      }

      setDeclineBookingId(null)
      setDeclineReason('')
      await refreshDashboard()
    } catch {
      setDeclineErrors([copy.actionFailed])
    } finally {
      setActionBookingId(null)
    }
  }

  async function handleStartBooking(bookingId: string) {
    setActionBookingId(bookingId)
    setPageErrors([])

    try {
      const { data } = await startBooking({ variables: { bookingId } })
      const result = data?.startBooking

      if (!result || result.errors.length > 0 || !result.booking) {
        setPageErrors(result?.errors.length ? result.errors : [copy.actionFailed])
        return
      }

      await refreshDashboard()
    } catch {
      setPageErrors([copy.actionFailed])
    } finally {
      setActionBookingId(null)
    }
  }

  async function handleVacationToggle() {
    if (!profile) return

    setPageErrors([])

    try {
      const { data } = await setVacationMode({
        variables: {
          onVacation: !profile.onVacation,
        },
      })
      const result = data?.setVacationMode

      if (!result || result.errors.length > 0 || !result.providerProfile) {
        setPageErrors(result?.errors.length ? result.errors : [copy.actionFailed])
        return
      }

      await refetchProfile()
    } catch {
      setPageErrors([copy.actionFailed])
    }
  }

  if (profileLoading) return <LoadingDashboard />

  if (profileError) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10 md:px-6">
        <SectionCard title={copy.loadError}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm leading-7 text-slate-600">{copy.loadError}</p>
            <Button type="button" onClick={() => refetchProfile()}>
              {copy.retry}
            </Button>
          </div>
        </SectionCard>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10 md:px-6">
        <SectionCard title={copy.missingProfileTitle}>
          <p className="text-sm leading-7 text-slate-600">{copy.missingProfileBody}</p>
        </SectionCard>
      </div>
    )
  }

  if (!isOnboardingComplete(profile)) {
    return <Navigate to="/provider/onboarding" replace />
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <section className="rounded-[2rem] bg-[linear-gradient(135deg,#0f172a_0%,#1d4ed8_54%,#14b8a6_100%)] p-8 text-white shadow-[0_24px_60px_-32px_rgba(15,23,42,0.85)] md:p-10">
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-cyan-200">{copy.eyebrow}</p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <h1 className="text-4xl font-black tracking-tight sm:text-5xl">{copy.pageTitle}</h1>
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white ring-1 ring-white/10">
            <ShieldCheck className="h-4 w-4" />
            {verificationLabel(profile, copy)}
          </span>
        </div>
        <p className="mt-4 max-w-3xl text-base leading-8 text-slate-100">{copy.pageBody}</p>
      </section>

      <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <StatCard
          title={copy.statsToday}
          value={String(todayBookingCount)}
          icon={<CalendarDays className="h-5 w-5" />}
          accent="primary"
        />
        <StatCard
          title={copy.statsWeek}
          value={formatCurrency(weeklyEarnings, activeLocale)}
          icon={<Wallet className="h-5 w-5" />}
          accent="emerald"
        />
        <StatCard
          title={copy.statsRating}
          value={profile.averageRating.toFixed(1)}
          detail={`${profile.totalReviews} ${copy.reviewCount}`}
          icon={<Star className="h-5 w-5" />}
          accent="amber"
        />
        <StatCard
          title={copy.statsPending}
          value={String(bookingsData?.pendingRequests.totalCount ?? 0)}
          icon={<BriefcaseBusiness className="h-5 w-5" />}
          accent="slate"
        />
        <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-400">{copy.vacationTitle}</p>
              <p className="mt-3 text-lg font-bold text-slate-950">
                {profile.onVacation ? copy.vacationOn : copy.vacationOff}
              </p>
              <p className="mt-2 text-sm text-slate-500">{copy.vacationBody}</p>
              {profile.vacationUntil && (
                <p className="mt-3 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                  {copy.vacationUntil}: {profile.vacationUntil}
                </p>
              )}
            </div>
            <button
              type="button"
              disabled={savingVacationMode}
              onClick={handleVacationToggle}
              className="rounded-full text-primary-600 transition hover:text-primary-700 disabled:cursor-not-allowed disabled:text-primary-300"
              aria-label={profile.onVacation ? copy.turnOff : copy.turnOn}
            >
              {profile.onVacation ? <ToggleRight className="h-10 w-10" /> : <ToggleLeft className="h-10 w-10" />}
            </button>
          </div>
        </div>
      </div>

      {pageErrors.length > 0 && (
        <div className="mt-6">
          <FormError errors={pageErrors} />
        </div>
      )}

      {bookingsError && (
        <div className="mt-6 rounded-[1.5rem] border border-red-100 bg-red-50 p-5 text-sm text-red-700">
          {copy.loadError}
        </div>
      )}

      <div className="mt-8 grid gap-8 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-8">
          <SectionCard title={copy.pendingTitle}>
            {bookingsLoading ? (
              <p className="text-sm text-slate-500">{copy.loadingTitle}</p>
            ) : pendingRequests.length === 0 ? (
              <EmptyState title={copy.noPendingTitle} body={copy.noPendingBody} />
            ) : (
              <div className="space-y-4">
                {pendingRequests.map((booking) => (
                  <BookingCard key={booking.id} booking={booking} locale={activeLocale} copy={copy}>
                    <Button
                      type="button"
                      loading={acceptingBooking && actionBookingId === booking.id}
                      onClick={() => handleAcceptBooking(booking.id)}
                    >
                      {copy.accept}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      loading={decliningBooking && actionBookingId === booking.id}
                      onClick={() => {
                        setDeclineErrors([])
                        setDeclineReason('')
                        setDeclineBookingId(booking.id)
                      }}
                    >
                      {copy.decline}
                    </Button>
                  </BookingCard>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title={copy.upcomingTitle}>
            {bookingsLoading ? (
              <p className="text-sm text-slate-500">{copy.loadingTitle}</p>
            ) : upcomingBookings.length === 0 ? (
              <EmptyState title={copy.noUpcomingTitle} body={copy.noUpcomingBody} />
            ) : (
              <div className="space-y-4">
                {upcomingBookings.map((booking) => (
                  <BookingCard key={booking.id} booking={booking} locale={activeLocale} copy={copy}>
                    {booking.scheduledDate === todayIso() && (
                      <Button
                        type="button"
                        loading={startingBooking && actionBookingId === booking.id}
                        onClick={() => handleStartBooking(booking.id)}
                      >
                        {copy.startJob}
                      </Button>
                    )}
                  </BookingCard>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title={copy.completedTitle}>
            {bookingsLoading ? (
              <p className="text-sm text-slate-500">{copy.loadingTitle}</p>
            ) : completedLast7Days.length === 0 ? (
              <EmptyState title={copy.noCompletedTitle} body={copy.noCompletedBody} />
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setShowCompleted((current) => !current)}
                  className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
                >
                  {showCompleted ? copy.hideCompleted : copy.showCompleted}
                </button>

                {showCompleted && (
                  <div className="mt-5 space-y-4">
                    {completedLast7Days.map((booking) => (
                      <BookingCard key={booking.id} booking={booking} locale={activeLocale} copy={copy} />
                    ))}
                  </div>
                )}
              </>
            )}
          </SectionCard>
        </div>

        <aside className="space-y-8 xl:sticky xl:top-24 xl:self-start">
          <SectionCard title={copy.quickLinksTitle}>
            <div className="space-y-3">
              {[
                {
                  to: '/provider/profile',
                  title: copy.quickLinkProfile,
                  body: copy.quickLinkProfileBody,
                  icon: <ShieldCheck className="h-5 w-5" />,
                },
                {
                  to: '/provider/availability',
                  title: copy.quickLinkAvailability,
                  body: copy.quickLinkAvailabilityBody,
                  icon: <Clock3 className="h-5 w-5" />,
                },
                {
                  to: '/provider/services',
                  title: copy.quickLinkServices,
                  body: copy.quickLinkServicesBody,
                  icon: <Home className="h-5 w-5" />,
                },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="group block rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5 transition hover:border-primary-200 hover:bg-white hover:shadow-sm"
                >
                  <div className="flex items-start justify-between gap-4">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
                      {link.icon}
                    </span>
                    <ArrowRight className="h-4 w-4 text-slate-400 transition group-hover:text-primary-600" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-slate-950">{link.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-slate-600">{link.body}</p>
                </Link>
              ))}
            </div>
          </SectionCard>
        </aside>
      </div>

      {declineBookingId && (
        <DeclineModal
          copy={copy}
          loading={decliningBooking && actionBookingId === declineBookingId}
          errors={declineErrors}
          reason={declineReason}
          onReasonChange={setDeclineReason}
          onClose={() => {
            setDeclineBookingId(null)
            setDeclineErrors([])
            setDeclineReason('')
          }}
          onConfirm={handleDeclineBooking}
        />
      )}
    </div>
  )
}
